import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { formatISO } from 'date-fns';

import { Injectable } from '@angular/core';

import { Store } from '@ngxs/store';

import { parseSubscriptionResponse } from '@app/settings/shared/modules/subscription/subscription.utilities';
import {
  ApiDeleteResponse,
  ApiGetObjectResponse,
  ApiPatchResponse,
  ApiPostResponse,
  CustomerApiRoute,
} from '../models/api.models';
import {
  AdspertPlanType,
  CustomerSubscription,
  InvoiceCurrency,
  PlanConstraints,
  SubscriptionInvoicePeriod,
} from '../models/customer.models';
import { StateService } from '../services/state.service';
import { ApiService } from './api.service';
import { CustomerApiService } from './customer-api.service';

@Injectable({ providedIn: 'root' })
export class CustomerSubscriptionApiService extends CustomerApiService {
  readonly customerRoute: CustomerApiRoute = CustomerApiRoute.Plan;

  constructor(api: ApiService, ss: StateService, store: Store) {
    super(api, ss, store);
  }

  getCustomerSubscription(): Observable<ApiGetObjectResponse<CustomerSubscription>> {
    return this.getObject<CustomerSubscription>().pipe(map((response) => parseSubscriptionResponse(response)));
  }

  getCustomerPlanConstraints(): Observable<ApiGetObjectResponse<PlanConstraints>> {
    return this.getObject<PlanConstraints>({
      route: [this.customerId, CustomerApiRoute.PlanConstraints],
    });
  }

  startCustomerSubscription(
    type: AdspertPlanType,
    currency?: InvoiceCurrency,
    preview?: boolean,
    patch?: boolean,
    renewalMonths: SubscriptionInvoicePeriod = SubscriptionInvoicePeriod.Monthly,
  ): Observable<ApiPostResponse<CustomerSubscription>> {
    const currency$ = currency
      ? of(currency)
      : this.getCustomerPlanConstraints().pipe(map((response) => response.data.currencies?.[0]));

    return currency$.pipe(
      switchMap((invoiceCurrency) => {
        const data = {
          type,
          ...(invoiceCurrency ? { currency: invoiceCurrency } : {}),
          preview: !!preview,
          renewalMonths,
        };

        return patch ? this.patch<CustomerSubscription>({ data }) : this.post<CustomerSubscription>({ data });
      }),
      map((response) => parseSubscriptionResponse(response)),
    );
  }

  deleteCustomerSubscription(): Observable<ApiDeleteResponse> {
    return this.delete({});
  }

  cancelCustomerSubscription(type: AdspertPlanType, endDate: Date): Observable<ApiPatchResponse<CustomerSubscription>> {
    return this.patch<CustomerSubscription>({
      data: { type, endDate: formatISO(endDate, { representation: 'date' }) },
    }).pipe(map((response) => parseSubscriptionResponse(response)));
  }

  restartCustomerSubscription(type: AdspertPlanType): Observable<ApiPatchResponse<CustomerSubscription>> {
    return this.patch<CustomerSubscription>({ data: { type, endDate: null } }).pipe(
      map((response) => parseSubscriptionResponse(response)),
    );
  }
}
